/* eslint-disable camelcase */
import React, { useState, useEffect, useContext } from "react"
import Header from "../../../layouts/Header"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Divider,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material"
import BackIcon from "../../../components/BackIcon"
import { useNavigate, useLocation } from "react-router-dom"
import { GlobalContext } from "../../../contexts/GlobalContext"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { useQuery, useMutation, api, showWarning, showSuccess } from "../../../api"
import getIconComponent from "../../utils/getIconComponent"

import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import SaveIcon from "@mui/icons-material/Save"
import PhoneIcon from "@mui/icons-material/Phone"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import RefreshIcon from "@mui/icons-material/Refresh"
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf"
import CloseIcon from "@mui/icons-material/Close"
import DownloadIcon from "@mui/icons-material/Download"

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1600px",
  margin: "0 auto",
  padding: "10px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
}

const extractArray = (res) => {
  if (Array.isArray(res?.data?.data?.data)) return res.data.data.data
  if (Array.isArray(res?.data?.data)) return res.data.data
  if (Array.isArray(res?.data)) return res.data
  if (Array.isArray(res)) return res
  return []
}

// Función auxiliar para forzar descarga
const descargarBase64PDF = (base64String, fileName) => {
  try {
    const validBase64 =
      base64String.startsWith("JVBER") || !base64String.includes("data:application/pdf;base64,")
        ? base64String
        : base64String.split("base64,")[1]

    const byteCharacters = atob(validBase64)
    const byteNumbers = new Array(byteCharacters.length)
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i)
    }
    const byteArray = new Uint8Array(byteNumbers)
    const blob = new Blob([byteArray], { type: "application/pdf" })

    const link = document.createElement("a")
    link.href = window.URL.createObjectURL(blob)
    link.download = `Documento_SRI_${fileName}.pdf`
    link.click()
  } catch (error) {
    console.error("Error al descargar PDF:", error)
    showWarning("No se pudo generar el archivo para descargar.")
  }
}

const DetalleGestiondeCartera = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { selectedMenuInfo } = useContext(GlobalContext)

  const [cliente, setCliente] = useState(null)
  const [cuotaSeleccionada, setCuotaSeleccionada] = useState(null)
  const [expandedAcc, setExpandedAcc] = useState(["panel1"])

  const [formData, setFormData] = useState({
    gestioncodigo: "",
    gesaccion: "",
    tipocompromiso: "",
    gesfono: "",
    gescompromiso: "",
    geshorcompromiso: "",
    gescomentario: "",
  })

  // Estados para el visor PDF
  const [pdfModalOpen, setPdfModalOpen] = useState(false)
  const [currentPdf, setCurrentPdf] = useState({ facnumfac: "", base64: "" })
  const [pdfLoading, setPdfLoading] = useState({}) // Controla loading individual por fila

  useEffect(() => {
    if (location.state?.cliente) setCliente(location.state.cliente)
    else navigate("/home/dashboard/GestiondeCartera")
  }, [location, navigate])

  // --- LÓGICA DEFENSIVA PARA CATÁLOGOS ---
  const { data: rawCatalogos, isLoading: isLoadingCatalogos } = useQuery({
    queryKey: ["getCatalogosGestion"],
    queryFn: async () => {
      try {
        const response = await api.post("/GestiondeCartera/getCatalogosGestion", {})
        return response?.data?.data || response?.data || response || {}
      } catch (error) {
        console.error("Error al cargar Catálogos:", error)
        return {}
      }
    },
    refetchOnWindowFocus: false,
  })

  const baseCatalogos = rawCatalogos?.gestiones ? rawCatalogos : rawCatalogos?.data ? rawCatalogos.data : {}

  const gestValidadas = Array.isArray(baseCatalogos?.gestiones) ? baseCatalogos.gestiones : []
  const listaGestiones = gestValidadas.map((item) => ({
    id: item.gestioncodigo?.trim() || "",
    label: item.gestiondescri?.trim() || "",
  }))

  const accValidadas = Array.isArray(baseCatalogos?.acciones) ? baseCatalogos.acciones : []
  const listaAcciones = accValidadas.map((item) => ({
    id: item.gesaccion?.trim() || "",
    label: item.gesaccion?.trim() || "",
  }))

  const compValidadas = Array.isArray(baseCatalogos?.compromisos) ? baseCatalogos.compromisos : []
  const listaCompromisos = compValidadas.map((item) => ({
    id: item.tipocompromiso?.trim() || "",
    label: item.tipocomdescri?.trim() || "",
  }))

  // === CONSULTA DE TODAS LAS CUOTAS DEL CLIENTE ===
  const {
    data: cuotas = [],
    isLoading: isLoadingCuotas,
    refetch: refetchCuotas,
  } = useQuery({
    queryKey: ["getDetalleCartera", cliente?.clicodigo],
    queryFn: async () => {
      if (!cliente?.clicodigo) return []
      const res = await api.post("/GestiondeCartera/getDetalleCarteraCliente", {
        clicodigo: cliente.clicodigo,
      })
      return extractArray(res)
    },
    enabled: !!cliente?.clicodigo,
    refetchOnWindowFocus: false,
  })

  // === CONSULTA DE CONTACTOS DINÁMICA ===
  // Se recalcula y refetch automático cada vez que cambia cuotaSeleccionada?.garcodigo
  const {
    data: contactos = [],
    isLoading: isLoadingContactos,
    refetch: refetchContactos,
  } = useQuery({
    queryKey: ["getContactosCliente", cliente?.clicodigo, cuotaSeleccionada?.garcodigo],
    queryFn: async () => {
      if (!cliente?.clicodigo) return []
      const res = await api.post("/GestiondeCartera/getContactosCliente", {
        clicodigo: cliente.clicodigo,
        garcodigo: cuotaSeleccionada?.garcodigo || null,
      })
      return extractArray(res)
    },
    enabled: !!cliente?.clicodigo,
    refetchOnWindowFocus: false,
  })

  const {
    data: historial = [],
    isFetching: isFetchingHist,
    isError: isErrorHist,
    refetch: refetchHist,
  } = useQuery({
    queryKey: ["getHistorialCuota", cuotaSeleccionada?.facnumfac, cuotaSeleccionada?.facsecuen],
    queryFn: async () => {
      if (!cuotaSeleccionada) return []
      const res = await api.post("/GestiondeCartera/getHistorialCuota", {
        facnumfac: cuotaSeleccionada.facnumfac,
        facsecuen: cuotaSeleccionada.facsecuen,
      })
      return extractArray(res)
    },
    enabled: !!cuotaSeleccionada,
    refetchOnWindowFocus: false,
    retry: 1,
  })

  const {
    data: detalleFactura = null,
    isFetching: isFetchingFactura,
    isError: isErrorFactura,
    refetch: refetchFactura,
  } = useQuery({
    queryKey: ["getDetalleFactura", cuotaSeleccionada?.facnumfac],
    queryFn: async () => {
      if (!cuotaSeleccionada) return null
      const res = await api.post("/GestiondeCartera/getDetalleFactura", { facnumfac: cuotaSeleccionada.facnumfac })
      let objData = res?.data || {}
      if (res?.data?.data?.cabecera) objData = res.data.data
      else if (res?.data?.cabecera) objData = res.data
      return objData
    },
    enabled: !!cuotaSeleccionada,
    refetchOnWindowFocus: false,
    retry: 1,
  })

  const { mutateAsync: GrabarGestion, isPending: isSaving } = useMutation({
    queryKey: ["isGrabarGestion"],
    fn: async (payload) => (await api.post("/GestiondeCartera/grabarGestionCartera", payload)).data,
    showError: "modal",
    onSuccess: () => {
      showSuccess("Gestión registrada correctamente.")
      setCuotaSeleccionada(null)
      setExpandedAcc(["panel1"])
      setFormData({
        gestioncodigo: "",
        gesaccion: "",
        tipocompromiso: "",
        gesfono: "",
        gescompromiso: "",
        geshorcompromiso: "",
        gescomentario: "",
      })
      refetchCuotas()
    },
  })

  const handleSeleccionarCuota = (row) => {
    // Si la cuota elegida tiene un garante distinto a la anterior, disparamos el refetch de contactos manualmente
    if (row.garcodigo !== cuotaSeleccionada?.garcodigo) {
      setTimeout(() => refetchContactos(), 0)
    }

    setCuotaSeleccionada(row)
    setFormData((prev) => ({
      ...prev,
      gestioncodigo: "",
      gesaccion: "",
      tipocompromiso: "",
      gesfono: "",
      gescomentario: "",
      gescompromiso: new Date().toISOString().split("T")[0],
      geshorcompromiso: "09:00",
    }))
    setExpandedAcc((prev) => {
      const newAcc = [...prev]
      if (!newAcc.includes("panel3")) newAcc.push("panel3")
      if (!newAcc.includes("panel4")) newAcc.push("panel4")
      return newAcc
    })
  }

  // Auto-seleccionar la primera cuota que devuelva el backend
  useEffect(() => {
    if (cuotas.length > 0 && !cuotaSeleccionada) {
      handleSeleccionarCuota(cuotas[0])
    }
  }, [cuotas, cuotaSeleccionada])

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedAcc((prev) => (isExpanded ? [...prev, panel] : prev.filter((p) => p !== panel)))
  }

  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      const newData = { ...prev, [field]: value }
      if (field === "tipocompromiso" && !value) {
        newData.gescompromiso = ""
        newData.geshorcompromiso = ""
      }
      if (field === "tipocompromiso" && value && !prev.gescompromiso) {
        newData.gescompromiso = new Date().toISOString().split("T")[0]
        newData.geshorcompromiso = "09:00"
      }
      return newData
    })
  }

  const handleSubmit = async (e) => {
    if (e) e.preventDefault()
    if (!cuotaSeleccionada) return showWarning("Debe esperar a que cargue la cuota.")
    if (!formData.gestioncodigo) return showWarning("Seleccione el Tipo de Gestión.")
    if (!formData.gesaccion) return showWarning("Seleccione la Acción / Resultado.")

    // VALIDACIÓN DE FECHA Y HORA DEL COMPROMISO
    if (formData.tipocompromiso) {
      const ahora = new Date()
      const fechaCompromiso = new Date(`${formData.gescompromiso}T${formData.geshorcompromiso}:00`)

      if (fechaCompromiso <= ahora) {
        return showWarning("La fecha y hora del compromiso deben ser estrictamente mayores a la fecha y hora actual.")
      }
    }

    const payload = {
      facnumfac: cuotaSeleccionada.facnumfac,
      facsecuen: cuotaSeleccionada.facsecuen,
      clicodigo: cliente.clicodigo,
      ...formData,
    }
    if (!formData.tipocompromiso) {
      payload.tipocompromiso = null
      payload.gescompromiso = null
      payload.geshorcompromiso = null
    }

    await GrabarGestion(payload)
  }

  // --- LÓGICA PARA VER EL PDF DESDE EL BACKEND ---
  const handleViewPDF = async (facnumfac, e) => {
    e.stopPropagation() // Para evitar que se seleccione la fila al clickear el botón del PDF
    setPdfLoading((prev) => ({ ...prev, [facnumfac]: true }))
    try {
      const res = await api.post("/GestiondeCartera/getFacturaPDF", { facnumfac })
      const data = res?.data || {}

      // Ajuste por si el backend devuelve envuelto en { data: "base64..." } o directo
      const base64Content = data.data || data

      if (!base64Content) {
        showWarning("Esta factura no tiene un documento electrónico SRI asociado o aún no ha sido autorizado.")
        return
      }

      setCurrentPdf({ facnumfac, base64: base64Content })
      setPdfModalOpen(true)
    } catch (error) {
      console.error(error)
      showWarning("Error de conexión al intentar obtener el documento PDF.")
    } finally {
      setPdfLoading((prev) => ({ ...prev, [facnumfac]: false }))
    }
  }

  // --- CÁLCULOS MATEMÁTICOS PARA TOTALES DE LA TABLA ---
  const totalDeuda = cuotas.reduce((acc, curr) => acc + (Number(curr.facsaldo) || 0), 0)
  const totalSaldoNeto = cuotas.reduce((acc, curr) => acc + (Number(curr.SaldoNeto) || 0), 0)
  const totalInteres = cuotas.reduce((acc, curr) => acc + (Number(curr.Interes) || 0), 0)
  const totalAPagar = totalSaldoNeto + totalInteres

  const action = selectedMenuInfo?.data?.barraAcciones?.find(
    (a) => a.acccaption === "GRABAR" || a.acccaption === "GUARDAR",
  )

  if (!cliente) return null

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        <Box sx={{ mb: 2 }}>
          <Tooltip title={action?.acccaption || "Grabar Gestión"}>
            <IconButton
              onClick={handleSubmit}
              disabled={isSaving || !cuotaSeleccionada}
              sx={{ border: "1px solid #ddd", bgcolor: "white", borderRadius: 1 }}
            >
              {action ? getIconComponent(action.accnameicono, action.acctipoico) : <SaveIcon />}
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Typography variant="h5" fontWeight="bold" color="primary">
            {cliente.clicodigo} - {cliente.clinombre}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 1 }}>
            Deuda Total Pendiente:{" "}
            <span style={{ fontWeight: "bold", color: "#b71c1c" }}>${totalDeuda.toFixed(2)}</span>
          </Typography>

          {contactos.length > 0 && (
            <Paper
              elevation={0}
              sx={{ display: "inline-flex", gap: 3, bgcolor: "#e3f2fd", px: 3, py: 1, borderRadius: 5 }}
            >
              <Typography
                variant="body2"
                sx={{ display: "flex", alignItems: "center", fontWeight: "bold", color: "#196C87" }}
              >
                <PhoneIcon fontSize="small" sx={{ mr: 0.5 }} /> {contactos[0].contelef1}{" "}
                {contactos[0].concelular ? ` / ${contactos[0].concelular}` : ""}
              </Typography>
            </Paper>
          )}
        </Box>

        <CustomBackdrop isLoading={isLoadingCuotas || isLoadingContactos || isSaving || isLoadingCatalogos} />

        <Box sx={StyledRoot}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <Accordion
                expanded={expandedAcc.includes("panel1")}
                onChange={handleAccordionChange("panel1")}
                sx={{ mb: 2, borderRadius: "8px !important", "&:before": { display: "none" }, boxShadow: 2 }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ bgcolor: "#f5f5f5", borderRadius: "8px 8px 0 0" }}
                >
                  <Typography variant="subtitle2" fontWeight="bold" color="primary">
                    1. CUOTAS A GESTIONAR (Clic para seleccionar)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer sx={{ maxHeight: 350 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <b>Sel.</b>
                          </TableCell>
                          <TableCell>
                            <b>Factura</b>
                          </TableCell>
                          <TableCell align="center">
                            <b>Cuota</b>
                          </TableCell>
                          <TableCell align="center">
                            <b>Emisión</b>
                          </TableCell>
                          <TableCell align="center">
                            <b>Vence</b>
                          </TableCell>
                          <TableCell align="right">
                            <b>Valor</b>
                          </TableCell>
                          <TableCell align="right">
                            <b>Abono</b>
                          </TableCell>
                          <TableCell align="right">
                            <b>Saldo</b>
                          </TableCell>
                          <TableCell align="right">
                            <b>Interés</b>
                          </TableCell>
                          <TableCell align="right">
                            <b>A Pagar</b>
                          </TableCell>
                          <TableCell align="center">
                            <b>Condición</b>
                          </TableCell>
                          {/* NUEVA COLUMNA PARA EL PDF */}
                          <TableCell align="center">
                            <b>Doc. SRI</b>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {cuotas.map((row, idx) => {
                          const isSelected =
                            cuotaSeleccionada?.facnumfac === row.facnumfac &&
                            cuotaSeleccionada?.facsecuen === row.facsecuen

                          const aPagar = Number(row.SaldoNeto || 0) + Number(row.Interes || 0)

                          return (
                            <TableRow
                              key={idx}
                              hover
                              onClick={() => handleSeleccionarCuota(row)}
                              sx={{
                                backgroundColor: isSelected ? "#e3f2fd" : "inherit",
                                cursor: "pointer",
                              }}
                            >
                              <TableCell>
                                {isSelected ? <CheckCircleIcon color="primary" fontSize="small" /> : null}
                              </TableCell>
                              <TableCell>{row.facnumfac}</TableCell>
                              <TableCell align="center">{row.facsecuen}</TableCell>
                              <TableCell align="center">{row.facfecemi}</TableCell>
                              <TableCell align="center">{row.facfecven}</TableCell>
                              <TableCell align="right">${Number(row.factotal || 0).toFixed(2)}</TableCell>
                              <TableCell align="right">${Number(row.facabono || 0).toFixed(2)}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: "bold" }}>
                                ${Number(row.SaldoNeto || 0).toFixed(2)}
                              </TableCell>
                              <TableCell align="right" sx={{ color: "error.main", fontWeight: "bold" }}>
                                ${Number(row.Interes || 0).toFixed(2)}
                              </TableCell>
                              <TableCell align="right" sx={{ color: "#b71c1c", fontWeight: "bold" }}>
                                ${aPagar.toFixed(2)}
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={row.condicion}
                                  size="small"
                                  color={row.condicion === "VENCIDA" ? "error" : "success"}
                                  variant={row.condicion === "VENCIDA" ? "filled" : "outlined"}
                                />
                              </TableCell>
                              {/* CELDA Y BOTÓN DEL VISOR PDF */}
                              <TableCell align="center">
                                <Tooltip title="Ver Documento PDF">
                                  <span>
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={(e) => handleViewPDF(row.facnumfac, e)}
                                      disabled={pdfLoading[row.facnumfac]}
                                    >
                                      {pdfLoading[row.facnumfac] ? (
                                        <CircularProgress size={18} />
                                      ) : (
                                        <PictureAsPdfIcon fontSize="small" />
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          )
                        })}
                        {/* FILA DE TOTALES */}
                        {cuotas.length > 0 && (
                          <TableRow sx={{ bgcolor: "#e0e0e0" }}>
                            <TableCell colSpan={7} align="right">
                              <b>TOTALES:</b>
                            </TableCell>
                            <TableCell align="right">
                              <b>${totalSaldoNeto.toFixed(2)}</b>
                            </TableCell>
                            <TableCell align="right" sx={{ color: "error.main" }}>
                              <b>${totalInteres.toFixed(2)}</b>
                            </TableCell>
                            <TableCell align="right" sx={{ color: "#b71c1c" }}>
                              <b>${totalAPagar.toFixed(2)}</b>
                            </TableCell>
                            <TableCell colSpan={2}></TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={expandedAcc.includes("panel2")}
                onChange={handleAccordionChange("panel2")}
                sx={{ mb: 2, borderRadius: "8px !important", "&:before": { display: "none" }, boxShadow: 2 }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ bgcolor: "#f5f5f5", borderRadius: "8px 8px 0 0" }}
                >
                  <Typography variant="subtitle2" fontWeight="bold" color="primary">
                    2. CONTACTOS DEL CLIENTE
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer sx={{ maxHeight: 200 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <b>Tipo</b>
                          </TableCell>
                          <TableCell>
                            <b>Contacto / Garante</b>
                          </TableCell>
                          <TableCell>
                            <b>Vínculo</b>
                          </TableCell>
                          <TableCell>
                            <b>Teléfono 1</b>
                          </TableCell>
                          <TableCell>
                            <b>Teléfono 2</b>
                          </TableCell>
                          <TableCell>
                            <b>Celular</b>
                          </TableCell>
                          <TableCell>
                            <b>Telf. Agencia 1</b>
                          </TableCell>
                          <TableCell>
                            <b>Telf. Agencia 2</b>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {contactos.map((c, idx) => (
                          <TableRow key={idx} hover>
                            <TableCell>
                              <Chip
                                label={c.tipo_contacto || "Contacto"}
                                size="small"
                                color={c.tipo_contacto === "Garante" ? "warning" : "default"}
                                variant="outlined"
                                sx={{ fontWeight: "bold", fontSize: "0.65rem", height: "20px" }}
                              />
                            </TableCell>
                            <TableCell>{c.condescri}</TableCell>
                            <TableCell>{c.areadescri || c.agendescri}</TableCell>
                            <TableCell
                              onClick={() => handleInputChange("gesfono", c.contelef1)}
                              sx={{ cursor: "pointer", color: "#196C87" }}
                            >
                              {c.contelef1}
                            </TableCell>
                            <TableCell
                              onClick={() => handleInputChange("gesfono", c.contelef2)}
                              sx={{ cursor: "pointer", color: "#196C87" }}
                            >
                              {c.contelef2}
                            </TableCell>
                            <TableCell
                              onClick={() => handleInputChange("gesfono", c.concelular)}
                              sx={{ cursor: "pointer", color: "#196C87", fontWeight: "bold" }}
                            >
                              {c.concelular}
                            </TableCell>
                            <TableCell
                              onClick={() => handleInputChange("gesfono", c.agentelef1)}
                              sx={{ cursor: "pointer", color: "#196C87" }}
                            >
                              {c.agentelef1}
                            </TableCell>
                            <TableCell
                              onClick={() => handleInputChange("gesfono", c.agentelef2)}
                              sx={{ cursor: "pointer", color: "#196C87" }}
                            >
                              {c.agentelef2}
                            </TableCell>
                          </TableRow>
                        ))}
                        {contactos.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={8} align="center">
                              No existen contactos registrados
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={expandedAcc.includes("panel3")}
                onChange={handleAccordionChange("panel3")}
                sx={{ mb: 2, borderRadius: "8px !important", "&:before": { display: "none" }, boxShadow: 2 }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ bgcolor: "#f5f5f5", borderRadius: "8px 8px 0 0" }}
                >
                  <Typography variant="subtitle2" fontWeight="bold" color="primary">
                    3. HISTORIAL DE GESTIÓN DE LA CUOTA SELECCIONADA
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {!cuotaSeleccionada ? (
                    <Typography color="text.secondary" align="center">
                      Seleccione una cuota para ver su historial.
                    </Typography>
                  ) : isFetchingHist ? (
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 3 }}>
                      <CircularProgress size={30} sx={{ mb: 1 }} />
                    </Box>
                  ) : isErrorHist ? (
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 2 }}>
                      <Typography color="error" sx={{ mb: 1 }}>
                        Error de conexión o el servidor tardó demasiado.
                      </Typography>
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<RefreshIcon />}
                        onClick={() => refetchHist()}
                      >
                        Reintentar Consulta
                      </Button>
                    </Box>
                  ) : (
                    <TableContainer sx={{ maxHeight: 250 }}>
                      <Table stickyHeader size="small">
                        <TableHead sx={{ bgcolor: "#eeeeee" }}>
                          <TableRow>
                            <TableCell>
                              <b>Fecha</b>
                            </TableCell>
                            <TableCell>
                              <b>Usuario</b>
                            </TableCell>
                            <TableCell>
                              <b>Gestión</b>
                            </TableCell>
                            <TableCell align="center">
                              <b>Estado</b>
                            </TableCell>
                            <TableCell>
                              <b>Compromiso</b>
                            </TableCell>
                            <TableCell>
                              <b>Comentario</b>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {historial.map((h, i) => (
                            <TableRow key={i}>
                              <TableCell>
                                {h.fecha} <br />
                                <span style={{ fontSize: "0.75rem", color: "gray" }}>{h.hora}</span>
                              </TableCell>
                              <TableCell>{h.usuario}</TableCell>
                              <TableCell>
                                {h.gestion}
                                <br />
                                <span style={{ fontSize: "0.75rem", color: "gray" }}>{h.accion}</span>
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={h.estado === "P" ? "PENDIENTE" : "FINALIZADA"}
                                  size="small"
                                  color={h.estado === "P" ? "warning" : "success"}
                                  variant={h.estado === "P" ? "filled" : "outlined"}
                                  sx={{ fontWeight: "bold", fontSize: "0.65rem", height: "20px" }}
                                />
                              </TableCell>
                              <TableCell>
                                {h.fechacompromiso ? (
                                  <span style={{ fontWeight: "bold", color: "#196C87" }}>
                                    {h.fechacompromiso} {h.horacompromiso}
                                  </span>
                                ) : (
                                  <span style={{ color: "#9e9e9e", fontStyle: "italic" }}>N/A</span>
                                )}
                              </TableCell>
                              <TableCell sx={{ fontSize: "0.8rem" }}>{h.comentario}</TableCell>
                            </TableRow>
                          ))}
                          {historial.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={6} align="center">
                                Sin historial previo para esta cuota.
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={expandedAcc.includes("panel4")}
                onChange={handleAccordionChange("panel4")}
                sx={{ mb: 2, borderRadius: "8px !important", "&:before": { display: "none" }, boxShadow: 2 }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ bgcolor: "#f5f5f5", borderRadius: "8px 8px 0 0" }}
                >
                  <Typography variant="subtitle2" fontWeight="bold" color="primary">
                    4. DETALLE DE LA FACTURA ASOCIADA
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ bgcolor: "#fcfcfc" }}>
                  {!cuotaSeleccionada ? (
                    <Typography color="text.secondary" align="center">
                      Cargando datos comerciales...
                    </Typography>
                  ) : isFetchingFactura ? (
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 3 }}>
                      <CircularProgress size={30} sx={{ mb: 1 }} />
                    </Box>
                  ) : isErrorFactura ? (
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 2 }}>
                      <Typography color="error" sx={{ mb: 1 }}>
                        Error al consultar el detalle de factura.
                      </Typography>
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<RefreshIcon />}
                        onClick={() => refetchFactura()}
                      >
                        Reintentar Consulta
                      </Button>
                    </Box>
                  ) : detalleFactura ? (
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            display: "flex",
                            gap: 4,
                            flexWrap: "wrap",
                            bgcolor: "#fff3e0",
                            border: "1px solid #ffe0b2",
                          }}
                        >
                          <Typography variant="body2">
                            <b>Pedido:</b> {detalleFactura.cabecera?.pednumped || "S/N"}
                          </Typography>
                          <Typography variant="body2">
                            <b>Vendedor:</b> {detalleFactura.cabecera?.vennombre || "S/N"}
                          </Typography>
                          <Typography variant="body2">
                            <b>Total Factura:</b> ${Number(detalleFactura.cabecera?.factotal || 0).toFixed(2)}
                          </Typography>
                          <Typography variant="body2">
                            <b>Saldo Factura:</b> ${Number(detalleFactura.cabecera?.facsaldo || 0).toFixed(2)}
                          </Typography>
                        </Paper>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="primary" fontWeight="bold" sx={{ mt: 1 }}>
                          ARTÍCULOS / INVENTARIO
                        </Typography>
                        <TableContainer component={Paper} sx={{ maxHeight: 200, mb: 2 }}>
                          <Table size="small">
                            <TableHead sx={{ bgcolor: "#eeeeee" }}>
                              <TableRow>
                                <TableCell>
                                  <b>Código</b>
                                </TableCell>
                                <TableCell>
                                  <b>Descripción</b>
                                </TableCell>
                                <TableCell align="center">
                                  <b>Cant.</b>
                                </TableCell>
                                <TableCell align="right">
                                  <b>Precio</b>
                                </TableCell>
                                <TableCell align="right">
                                  <b>SubTotal</b>
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {detalleFactura.articulos?.map((art, i) => (
                                <TableRow key={i} hover>
                                  <TableCell>{art.artcodigo}</TableCell>
                                  <TableCell>{art.artdescri}</TableCell>
                                  <TableCell align="center">{art.faccantidad}</TableCell>
                                  <TableCell align="right">${Number(art.facpreven || 0).toFixed(2)}</TableCell>
                                  <TableCell align="right">${Number(art.subtotal || 0).toFixed(2)}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>

                        <Typography variant="subtitle2" color="primary" fontWeight="bold">
                          CUOTA / DIVIDENDO ESPECÍFICO
                        </Typography>
                        <TableContainer component={Paper} sx={{ maxHeight: 200, mb: 2 }}>
                          <Table size="small">
                            <TableHead sx={{ bgcolor: "#eeeeee" }}>
                              <TableRow>
                                <TableCell align="center">
                                  <b>Cuota</b>
                                </TableCell>
                                <TableCell>
                                  <b>Vencimiento</b>
                                </TableCell>
                                <TableCell align="right">
                                  <b>Valor</b>
                                </TableCell>
                                <TableCell align="right">
                                  <b>Abono</b>
                                </TableCell>
                                <TableCell align="right">
                                  <b>Saldo</b>
                                </TableCell>
                                <TableCell>
                                  <b>Estado Legal</b>
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {detalleFactura.cartera
                                ?.filter((c) => c.facsecuen === cuotaSeleccionada.facsecuen)
                                .map((c, i) => (
                                  <TableRow key={i} hover>
                                    <TableCell align="center">{c.facsecuen}</TableCell>
                                    <TableCell>{c.facfecven}</TableCell>
                                    <TableCell align="right">${Number(c.factotal || 0).toFixed(2)}</TableCell>
                                    <TableCell align="right">${Number(c.facabono || 0).toFixed(2)}</TableCell>
                                    <TableCell align="right" sx={{ color: c.facsaldo > 0 ? "red" : "green" }}>
                                      ${Number(c.facsaldo || 0).toFixed(2)}
                                    </TableCell>
                                    <TableCell>{c.legal}</TableCell>
                                  </TableRow>
                                ))}
                            </TableBody>
                          </Table>
                        </TableContainer>

                        <Typography variant="subtitle2" color="primary" fontWeight="bold">
                          COBROS REALIZADOS (De toda la factura)
                        </Typography>
                        <TableContainer component={Paper} sx={{ maxHeight: 200 }}>
                          <Table size="small">
                            <TableHead sx={{ bgcolor: "#eeeeee" }}>
                              <TableRow>
                                <TableCell>
                                  <b>Comprobante</b>
                                </TableCell>
                                <TableCell>
                                  <b>Fecha</b>
                                </TableCell>
                                <TableCell>
                                  <b>Tipo</b>
                                </TableCell>
                                <TableCell align="right">
                                  <b>Valor</b>
                                </TableCell>
                                <TableCell>
                                  <b>Banco/Cheque</b>
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {detalleFactura.cobros?.map((c, i) => (
                                <TableRow key={i} hover>
                                  <TableCell>{c.cxcnumcomp}</TableCell>
                                  <TableCell>{c.cxcfecemi}</TableCell>
                                  <TableCell>{c.cxctippag}</TableCell>
                                  <TableCell align="right">${Number(c.cxcvalreal || 0).toFixed(2)}</TableCell>
                                  <TableCell>
                                    {c.bcodescri} {c.cxcnumche}
                                  </TableCell>
                                </TableRow>
                              ))}
                              {detalleFactura.cobros?.length === 0 && (
                                <TableRow>
                                  <TableCell colSpan={5} align="center">
                                    No existen cobros registrados.
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Grid>
                    </Grid>
                  ) : null}
                </AccordionDetails>
              </Accordion>
            </Grid>

            {/* COLUMNA DERECHA: FORMULARIO */}
            <Grid item xs={12} md={4}>
              <Box sx={{ position: "sticky", top: "80px" }}>
                <Paper elevation={3} sx={{ p: 3, borderRadius: 2, bgcolor: cuotaSeleccionada ? "#ffffff" : "#f9f9f9" }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    sx={{ mb: 2, color: cuotaSeleccionada ? "#196C87" : "#9e9e9e" }}
                  >
                    Registro de Gestión
                  </Typography>
                  <Divider sx={{ mb: 3 }} />

                  {!cuotaSeleccionada ? (
                    <Box sx={{ textAlign: "center", mt: 5, color: "#9e9e9e" }}>
                      <Typography>Cargando información del registro seleccionado...</Typography>
                    </Box>
                  ) : (
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Autocomplete
                          options={listaGestiones}
                          getOptionLabel={(option) => option.label || ""}
                          value={
                            listaGestiones.find((c) => c.id === formData.gestioncodigo) ||
                            (formData.gestioncodigo
                              ? { id: formData.gestioncodigo, label: `${formData.gestioncodigo}` }
                              : null)
                          }
                          onChange={(event, newValue) =>
                            handleInputChange("gestioncodigo", newValue ? newValue.id : "")
                          }
                          isOptionEqualToValue={(option, value) => option.id === value?.id}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Tipo de Gestión *"
                              placeholder="Buscar..."
                              InputLabelProps={{ shrink: true }}
                              size="small"
                            />
                          )}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Autocomplete
                          options={listaAcciones}
                          getOptionLabel={(option) => option.label || ""}
                          value={
                            listaAcciones.find((c) => c.id === formData.gesaccion) ||
                            (formData.gesaccion ? { id: formData.gesaccion, label: `${formData.gesaccion}` } : null)
                          }
                          onChange={(event, newValue) => handleInputChange("gesaccion", newValue ? newValue.id : "")}
                          isOptionEqualToValue={(option, value) => option.id === value?.id}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Acción / Resultado *"
                              placeholder="Buscar..."
                              InputLabelProps={{ shrink: true }}
                              size="small"
                            />
                          )}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Autocomplete
                          options={listaCompromisos}
                          getOptionLabel={(option) => option.label || ""}
                          value={
                            listaCompromisos.find((c) => c.id === formData.tipocompromiso) ||
                            (formData.tipocompromiso
                              ? { id: formData.tipocompromiso, label: `${formData.tipocompromiso}` }
                              : null)
                          }
                          onChange={(event, newValue) =>
                            handleInputChange("tipocompromiso", newValue ? newValue.id : "")
                          }
                          isOptionEqualToValue={(option, value) => option.id === value?.id}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Tipo de Compromiso"
                              placeholder="N/A (Sin compromiso)"
                              InputLabelProps={{ shrink: true }}
                              size="small"
                            />
                          )}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Teléfono (Clic en tabla Contactos)"
                          value={formData.gesfono}
                          onChange={(e) => handleInputChange("gesfono", e.target.value)}
                          size="small"
                        />
                      </Grid>

                      <Grid item xs={6}>
                        <TextField
                          type="date"
                          fullWidth
                          label="F. Compromiso"
                          InputLabelProps={{ shrink: true }}
                          value={formData.gescompromiso}
                          onChange={(e) => handleInputChange("gescompromiso", e.target.value)}
                          size="small"
                          disabled={!formData.tipocompromiso}
                        />
                      </Grid>

                      <Grid item xs={6}>
                        <TextField
                          type="time"
                          fullWidth
                          label="Hora"
                          InputLabelProps={{ shrink: true }}
                          value={formData.geshorcompromiso}
                          onChange={(e) => handleInputChange("geshorcompromiso", e.target.value)}
                          size="small"
                          disabled={!formData.tipocompromiso}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          multiline
                          rows={4}
                          fullWidth
                          label="Comentario u Observaciones"
                          value={formData.gescomentario}
                          onChange={(e) => handleInputChange("gescomentario", e.target.value.toUpperCase())}
                          size="small"
                        />
                      </Grid>
                    </Grid>
                  )}
                </Paper>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* ============================================================ */}
        {/* MODAL DEL VISOR DE PDF NATIVO                                */}
        {/* ============================================================ */}
        <Dialog
          open={pdfModalOpen}
          onClose={() => setPdfModalOpen(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{ sx: { height: "90vh", display: "flex", flexDirection: "column" } }}
        >
          <DialogTitle
            sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", bgcolor: "#f5f5f5" }}
          >
            <Typography variant="h6" fontWeight="bold" color="primary">
              Documento SRI - {currentPdf.facnumfac}
            </Typography>
            <IconButton onClick={() => setPdfModalOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <Divider />

          <DialogContent sx={{ p: 0, flexGrow: 1, overflow: "hidden", backgroundColor: "#525659" }}>
            {currentPdf.base64 && (
              <iframe
                title="Visor PDF"
                src={
                  currentPdf.base64.startsWith("JVBER") || !currentPdf.base64.includes("data:application/pdf;base64,")
                    ? `data:application/pdf;base64,${currentPdf.base64}`
                    : currentPdf.base64
                }
                width="100%"
                height="100%"
                style={{ border: "none" }}
              />
            )}
          </DialogContent>

          <DialogActions sx={{ p: 2, bgcolor: "#f5f5f5" }}>
            <Button onClick={() => setPdfModalOpen(false)} color="inherit" sx={{ fontWeight: "bold" }}>
              Cerrar
            </Button>
            <Button
              onClick={() => descargarBase64PDF(currentPdf.base64, currentPdf.facnumfac)}
              variant="contained"
              color="primary"
              startIcon={<DownloadIcon />}
              sx={{ fontWeight: "bold" }}
            >
              Descargar Archivo
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </ThemeProvider>
  )
}

export default DetalleGestiondeCartera
